import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { CreateInseparableRedemptionDto, TransactionType } from '@dealstack/shared/lib/generated';
import { previewInseparableRedemption } from '@dealstack/frontend/app/services/Transactions.service.ts';
import {
	getBackendErrorMessage,
	isStandardBackendError,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer.ts';
import type {
	RedemptionCounterpartInput,
	RedemptionCounterpartPreview,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useRedemptionCounterpartPreview.types.ts';

/**
 * CAP-2039: who else loses title, answered WHILE the form is being filled in.
 *
 * In a nominee structure the counterpart is a fact of the legal structure, not a choice — if an
 * investor redeems their beneficial interest, the nominee's legal leg IS the other side. Only the
 * resolver knows which holder that is, and until now the user found out after pressing Save, in a
 * confirmation modal. That is too late to be guidance: by then they have committed to an operation
 * whose consequence they could not see, and a refusal reads as the form rejecting them rather than
 * as a fact about the register.
 *
 * So the preview runs at field-settle instead. It is write-free, and a rejection is information
 * ("this holder has no counterpart for those shares") rather than a failure, which is why every
 * refusal renders inline and none of them toasts — 120247 fires on essentially every keystroke while
 * a unit count is being typed past the holding.
 *
 * Debounced because each call pays a full fold, which is ~1s+ on a production-scale nominee deal.
 * The query key is the settled input, so React Query dedupes and caches: correcting a typo back to a
 * previous value answers instantly rather than re-folding.
 */
const DEBOUNCE_MS = 500;

/** Serialises the input into the query key, so a value the user typed and undid is a cache hit. */
const keyOf = (input: RedemptionCounterpartInput): string =>
	JSON.stringify([
		input.securityId ?? '',
		input.holderId ?? '',
		input.interest ?? '',
		input.units ?? 0,
		input.fromMipId ?? '',
		input.soldForId ?? '',
		input.type ?? '',
		input.replacesRedemptionGroupId ?? '',
	]);

/**
 * The question, or null when the form does not yet pose one. Date and cost are deliberately NOT
 * required: neither changes which counterpart holds the title, so demanding them would keep the
 * panel blank through most of the form, and the resolver accepts a placeholder for both.
 */
export const toPayload = (
	input: RedemptionCounterpartInput
): CreateInseparableRedemptionDto | null => {
	const { securityId, holderId, interest } = input;
	const units = Number(input.units ?? 0);
	if (!securityId || !holderId || !interest || units < 1) {
		return null;
	}
	return {
		date: (input.date ? new Date(input.date) : new Date()).toISOString(),
		securityId,
		holderId,
		interest,
		units,
		cost: Number(input.cost ?? 0),
		type:
			input.type === TransactionType.CANCELLATION
				? TransactionType.CANCELLATION
				: TransactionType.REDEMPTION,
		// Key PRESENCE is what tells the resolver an axis was named, so an unset one is omitted
		// rather than sent as null — null selects the partition whose value IS null.
		...(input.fromMipId ? { fromMipId: input.fromMipId } : {}),
		...(input.soldForId ? { soldForId: input.soldForId } : {}),
		...(input.replacesRedemptionGroupId
			? { replacesRedemptionGroupId: input.replacesRedemptionGroupId }
			: {}),
	};
};

export const useRedemptionCounterpartPreview = (
	input: RedemptionCounterpartInput,
	enabled: boolean
): RedemptionCounterpartPreview => {
	const key = keyOf(input);
	const [settledKey, setSettledKey] = useState(key);

	useEffect(() => {
		const timer = setTimeout(() => setSettledKey(key), DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [key]);

	// Built from the CURRENT input but only SENT once its key has settled, so a keystroke burst
	// issues one request rather than one per character.
	const payload = useMemo(() => toPayload(input), [key]);

	const isCurrent = settledKey === key;
	const query = useQuery({
		queryKey: [API_PATHS.CAP_TRANSACTION_INSEPARABLE_REDEMPTION_PREVIEW_POST, settledKey],
		queryFn: () => (payload ? previewInseparableRedemption(payload) : undefined),
		enabled: enabled && isCurrent && payload !== null,
		// A refusal is the answer, not a fault: it is rendered in the panel. Retrying would also mean
		// re-folding on every keystroke past the holding.
		retry: false,
		meta: { suppressErrorToast: true },
		staleTime: 30_000,
	});

	return useMemo((): RedemptionCounterpartPreview => {
		if (!enabled || payload === null) {
			return { status: 'idle', legs: [], spansMultiplePositions: false };
		}
		if (!isCurrent || query.isFetching) {
			return { status: 'loading', legs: [], spansMultiplePositions: false };
		}
		if (query.error) {
			return {
				status: 'refused',
				legs: [],
				spansMultiplePositions: false,
				message: isStandardBackendError(query.error)
					? getBackendErrorMessage(query.error)
					: undefined,
			};
		}
		const legs = query.data?.legs ?? [];
		if (!legs.length) {
			return { status: 'idle', legs: [], spansMultiplePositions: false };
		}
		const positions = new Set(
			legs.map((leg) => `${leg.sourceMipId ?? ''}|${leg.sourceHeldForId ?? ''}`)
		);
		return {
			status: 'resolved',
			legs,
			spansMultiplePositions: positions.size > 1,
		};
	}, [enabled, payload, isCurrent, query.isFetching, query.error, query.data]);
};
